import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { nationalHubService } from '../api/services/national-hub.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { SelectPicker } from '../components/ui/select-picker'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { ListPagination } from '../components/ui/list-pagination'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { flattenDepartmentTree } from '../lib/department-tree'
import type { NamedRef } from '../types/case'
import type { NationalHubCase } from '../types/national-hub'

function formatLabel(value: string | null | undefined) {
  if (!value) {
    return '—'
  }

  return value.replace(/_/g, ' ')
}

function filterOptions(items: NamedRef[]) {
  return [
    { value: '', label: 'All' },
    ...items.map((item) => ({ value: item.id, label: item.name })),
  ]
}

export function NationalHubPage() {
  const navigate = useNavigate()
  const [cases, setCases] = useState<NationalHubCase[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [departments, setDepartments] = useState<NamedRef[]>([])
  const [procedures, setProcedures] = useState<NamedRef[]>([])
  const [procedureId, setProcedureId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [outcome, setOutcome] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [appliedFilters, setAppliedFilters] = useState({
    procedure_id: '',
    department_id: '',
    outcome: '',
    date_from: '',
    date_to: '',
  })

  useEffect(() => {
    void (async () => {
      try {
        const response = await nationalHubService.filters()
        const flatDepartments = flattenDepartmentTree(response.data.departments ?? []).map(
          (item) => ({ id: item.id, name: item.label }),
        )
        setDepartments(flatDepartments)
        setProcedures(response.data.procedures ?? [])
      } catch {
        setDepartments([])
        setProcedures([])
      }
    })()
  }, [])

  const loadCases = useCallback(async () => {
    try {
      const response = await nationalHubService.list({
        page,
        per_page: perPage,
        procedure_id: appliedFilters.procedure_id || undefined,
        department_id: appliedFilters.department_id || undefined,
        outcome: appliedFilters.outcome || undefined,
        date_from: appliedFilters.date_from || undefined,
        date_to: appliedFilters.date_to || undefined,
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setCases(items)
      setMeta(nextMeta)
    } catch {
      setCases([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [appliedFilters, page, perPage])

  useEffect(() => {
    void loadCases()
  }, [loadCases])

  const applyFilters = () => {
    setPage(1)
    setAppliedFilters({
      procedure_id: procedureId,
      department_id: departmentId,
      outcome: outcome.trim(),
      date_from: dateFrom,
      date_to: dateTo,
    })
  }

  const clearFilters = () => {
    setProcedureId('')
    setDepartmentId('')
    setOutcome('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
    setAppliedFilters({
      procedure_id: '',
      department_id: '',
      outcome: '',
      date_from: '',
      date_to: '',
    })
  }

  const openCase = (id: string) => {
    navigate(`/national-hub/${id}`)
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">National hub</h1>
          <p className="mt-1 text-sm text-ink-600">
            Browse anonymised clinical cases shared for national learning.
          </p>
        </div>

        <section className="mb-6 rounded-lg border border-parch-100 bg-white p-4 shadow-card">
          <p className="mb-4 text-sm font-medium text-ink-700">Filters</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <FieldLabel>Procedure</FieldLabel>
              <SelectPicker
                options={filterOptions(procedures)}
                value={procedureId}
                onChange={setProcedureId}
                placeholder="All procedures"
              />
            </div>
            <div>
              <FieldLabel>Department</FieldLabel>
              <SelectPicker
                options={filterOptions(departments)}
                value={departmentId}
                onChange={setDepartmentId}
                placeholder="All departments"
              />
            </div>
            <div>
              <FieldLabel>Outcome</FieldLabel>
              <Input
                value={outcome}
                onChange={(event) => setOutcome(event.target.value)}
                placeholder="Search outcome…"
              />
            </div>
            <div>
              <FieldLabel>Procedure date from</FieldLabel>
              <Input
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
              />
            </div>
            <div>
              <FieldLabel>Procedure date to</FieldLabel>
              <Input
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={applyFilters}>
              Apply Filters
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Age</th>
                  <th className="px-4 py-3 font-medium">Gender</th>
                  <th className="px-4 py-3 font-medium">Procedure</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">Procedure date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Outcome</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && cases.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-sm text-ink-500">
                      No cases found.
                    </td>
                  </tr>
                ) : (
                  cases.map((hubCase) => (
                    <tr
                      key={hubCase.id}
                      className="cursor-pointer border-b border-parch-50 last:border-0 hover:bg-parch-50/60"
                      onClick={() => openCase(hubCase.id)}
                    >
                      <td className="px-4 py-3">{hubCase.age ?? '—'}</td>
                      <td className="px-4 py-3 capitalize">{formatLabel(hubCase.gender)}</td>
                      <td className="px-4 py-3">{hubCase.procedure?.name ?? '—'}</td>
                      <td className="px-4 py-3">{hubCase.department?.name ?? '—'}</td>
                      <td className="px-4 py-3">{hubCase.procedure_date ?? '—'}</td>
                      <td className="px-4 py-3 capitalize">{formatLabel(hubCase.status)}</td>
                      <td className="max-w-xs truncate px-4 py-3">{hubCase.outcome ?? '—'}</td>
                      <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                        <PermissionGate permission="national-hub-show">
                          <TableIconActions>
                            <IconActionButton
                              icon={Eye}
                              label="View"
                              onClick={() => openCase(hubCase.id)}
                            />
                          </TableIconActions>
                        </PermissionGate>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(next) => {
            setPerPage(next)
            setPage(1)
          }}
        />
      </div>
    </AppShell>
  )
}
